import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ShipmentSchema, ShipmentCreateInputSchema, ShipmentUpdateInputSchema, type Shipment, type ShipmentCreateInput, type ShipmentUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `shipment` (mouvement physique de biens, org-wide).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`ShipmentCreateInputSchema`) AVANT toute écriture Firestore. Les liens vers
 * d'autres familles (order, stock-movement, route, inspection, contact) sont de
 * simples ids.
 */

export type { ShipmentCreateInput };

/** Nom de la collection Firestore de la famille shipment. */
export const SHIPMENTS_COLLECTION = 'shipments';

/**
 * Repository typé pour la famille `shipment`.
 * Toute lecture est validée par `ShipmentSchema`, toute écriture par
 * `ShipmentCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface ShipmentRepository {
  create(input: ShipmentCreateInput): Promise<Shipment>;
  update(id: string, patch: ShipmentUpdateInput): Promise<Shipment>;
  list(options?: ListOptions): Promise<Shipment[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Shipment | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Shipment>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Shipment>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Shipment>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const shipmentConverter = createConverter<Shipment>((raw: DocumentData) => ShipmentSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createShipmentRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ShipmentRepository {
  const col = collection(db, SHIPMENTS_COLLECTION).withConverter(shipmentConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Shipment>(db, col, SHIPMENTS_COLLECTION, getActorId);

  return {
    async create(input: ShipmentCreateInput): Promise<Shipment> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = ShipmentCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.direction !== undefined) optional['direction'] = fields.direction;
      if (fields.carrier !== undefined) optional['carrier'] = fields.carrier;
      if (fields.trackingNumber !== undefined) optional['trackingNumber'] = fields.trackingNumber;
      if (fields.scheduledAt !== undefined) optional['scheduledAt'] = fields.scheduledAt;
      if (fields.deliveredAt !== undefined) optional['deliveredAt'] = fields.deliveredAt;
      if (fields.destinationRef !== undefined) optional['destinationRef'] = fields.destinationRef;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.stockMovementId !== undefined) optional['stockMovementId'] = fields.stockMovementId;
      if (fields.routeId !== undefined) optional['routeId'] = fields.routeId;
      if (fields.inspectionId !== undefined) optional['inspectionId'] = fields.inspectionId;
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Shipment);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (scheduledAt/deliveredAt sont
      // déjà des Date issues de l'entrée validée).
      return ShipmentSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: ShipmentUpdateInput): Promise<Shipment> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = ShipmentUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, SHIPMENTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`shipment ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Shipment[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Shipment | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
